import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, Save, RefreshCw, AlertTriangle, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../../api';
import { ScoringCriteria } from '../../types';

const ScoringAdminPage: React.FC = () => {
  const [criteria, setCriteria] = useState<ScoringCriteria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState('');

  // Track edits
  const [editedWeights, setEditedWeights] = useState<Record<string, number>>({});

  const fetchCriteria = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/scoring/criteria');
      const data = res.data.data || res.data || [];
      setCriteria(data);
    } catch (err) {
      setError('Erreur lors du chargement des critères de scoring.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  const handleWeightChange = (id: string, weight: number) => {
    setEditedWeights((prev) => ({ ...prev, [id]: weight }));
  };

  const handleToggleActive = async (criterion: ScoringCriteria) => {
    try {
      await api.patch(`/scoring/criteria/${criterion.id}`, { isActive: !criterion.isActive });
      fetchCriteria();
    } catch (err) {
      // ignore
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const promises = Object.entries(editedWeights).map(([id, weight]) =>
        api.patch(`/scoring/criteria/${id}`, { weight })
      );
      await Promise.all(promises);
      setSaveMessage('Pondérations sauvegardées avec succès.');
      setEditedWeights({});
      fetchCriteria();
    } catch (err) {
      setSaveMessage('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalcMessage('');
    try {
      await api.post('/scoring/recalculate');
      setRecalcMessage('Recalcul lancé avec succès. Les scores seront mis à jour prochainement.');
    } catch (err: any) {
      setRecalcMessage(err.response?.data?.message || 'Erreur lors du recalcul.');
    } finally {
      setRecalculating(false);
    }
  };

  // Group criteria by category
  const groupedCriteria = criteria.reduce((acc, c) => {
    const cat = c.category || 'Général';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {} as Record<string, ScoringCriteria[]>);

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><BarChart3 size={28} /> Scoring</span>
        </h1>
        <div className="page-header-actions">
          <button
            className="btn"
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            {recalculating ? <span className="spinner spinner-sm" /> : <RefreshCw size={16} />}
            Recalculer tous les scores
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || Object.keys(editedWeights).length === 0}
          >
            {saving ? <span className="spinner spinner-sm" /> : <Save size={16} />}
            Sauvegarder
          </button>
        </div>
      </div>

      {saveMessage && (
        <div
          className={`mb-3 ${saveMessage.includes('succès') ? 'badge-success' : 'badge-danger'}`}
          style={{ padding: '10px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
        >
          {saveMessage}
        </div>
      )}

      {recalcMessage && (
        <div
          className={`mb-3 ${recalcMessage.includes('succès') ? 'badge-success' : 'badge-danger'}`}
          style={{ padding: '10px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
        >
          {recalcMessage}
        </div>
      )}

      {loading ? (
        <div className="spinner-container"><div className="spinner" /></div>
      ) : error ? (
        <div className="empty-state">
          <AlertTriangle size={48} />
          <p className="empty-state-title">{error}</p>
        </div>
      ) : criteria.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <BarChart3 size={48} />
              <p className="empty-state-title">Aucun critère de scoring configuré</p>
            </div>
          </div>
        </div>
      ) : (
        Object.entries(groupedCriteria).map(([category, items]) => (
          <div key={category} className="card mb-3">
            <div className="card-header">
              <h3>{category}</h3>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Critère</th>
                      <th>Clé</th>
                      <th>Description</th>
                      <th>Type de valeur</th>
                      <th style={{ width: '120px' }}>Pondération</th>
                      <th style={{ width: '100px' }}>Actif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id} style={{ opacity: c.isActive ? 1 : 0.5 }}>
                        <td className="font-semibold">{c.name}</td>
                        <td><code style={{ fontSize: '0.8rem', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>{c.key}</code></td>
                        <td className="text-secondary">{c.description || '-'}</td>
                        <td>
                          <span className="badge badge-gray">{c.valueType}</span>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input"
                            value={editedWeights[c.id] !== undefined ? editedWeights[c.id] : c.weight}
                            onChange={(e) => handleWeightChange(c.id, Number(e.target.value))}
                            min={0}
                            max={100}
                            step={1}
                            style={{ width: '80px', padding: '4px 8px' }}
                          />
                        </td>
                        <td>
                          <button
                            className="btn-icon"
                            onClick={() => handleToggleActive(c)}
                            title={c.isActive ? 'Désactiver' : 'Activer'}
                          >
                            {c.isActive ? (
                              <ToggleRight size={24} color="#10B981" />
                            ) : (
                              <ToggleLeft size={24} color="#9CA3AF" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ScoringAdminPage;
